"use client";
import { useEffect, useState, useCallback } from "react";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getDatabase, ref as dbRef, get, child } from "firebase/database";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useCurrentUid } from "../hooks/useCurrentUid";
import "./POPage.css";
import { MdInfo } from "react-icons/md";

const UserPurchaseOrders = () => {
  const storage = getStorage();
  const db = getDatabase();
  const userId = useCurrentUid();

  const [poList, setPoList] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [selectedPO, setSelectedPO] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [quarterPOs, setQuarterPOs] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile for preview behavior
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getFinancialQuarter = () => {
    const m = new Date().getMonth() + 1;
    if (m >= 4 && m <= 6) return { q: "Q1", r: "Apr–Jun" };
    if (m >= 7 && m <= 9) return { q: "Q2", r: "Jul–Sep" };
    if (m >= 10 && m <= 12) return { q: "Q3", r: "Oct–Dec" };
    return { q: "Q4", r: "Jan–Mar" };
  };

  const { q, r } = getFinancialQuarter();

  const applyFilter = (po) => {
    const poDate = new Date(po.uploadedAt);
    const now = new Date();
    if (filter === "WEEK") return (now - poDate) / (86400000) <= 7;
    if (filter === "MONTH")
      return poDate.getMonth() === now.getMonth() && poDate.getFullYear() === now.getFullYear();
    return true;
  };

  const getQuarterPOs = useCallback(() => {
    const now = new Date();
    const month = now.getMonth();
    let start, end;
    if (month >= 3 && month <= 5) { start = 3; end = 5; }
    else if (month >= 6 && month <= 8) { start = 6; end = 8; }
    else if (month >= 9 && month <= 11) { start = 9; end = 11; }
    else { start = 0; end = 2; }

    return poList.filter((po) => {
      const poMonth = new Date(po.uploadedAt).getMonth();
      const poYear = new Date(po.uploadedAt).getFullYear();
      return poMonth >= start && poMonth <= end && poYear === now.getFullYear();
    });
  }, [poList]);

  const fetchPOs = async () => {
    if (!userId) {
      setPoList([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const snapshot = await get(child(dbRef(db), `ActivePO/${userId}`));
      if (!snapshot.exists()) {
        setPoList([]);
        return;
      }
      const data = snapshot.val();
      const list = Object.entries(data).map(([key, value]) => ({
        id: key,
        name: value.fileName,
        uploadedAt: value.uploadedAt,
        storagePath: value.storagePath,
      }));
      list.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      setPoList(list);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs();
  }, [userId]);

  const openExportModal = () => {
    const qPOs = getQuarterPOs();
    if (qPOs.length === 0) {
      alert(`No POs in current quarter (${q}: ${r})`);
      return;
    }
    setQuarterPOs(qPOs);
    setShowExportModal(true);
  };

  // FIXED: Proper CORS-safe fetch with no-cors fallback + error handling
 const exportPOsAsZip = async () => {
  if (exporting || quarterPOs.length === 0) return;

  setExporting(true);
  setProgress(0);

  try {
    const zip = new JSZip();
    const folder = zip.folder("Purchase_Orders");

    for (let i = 0; i < quarterPOs.length; i++) {
      const po = quarterPOs[i];

      // Get signed URL
      const url = await getDownloadURL(ref(storage, po.storagePath));

      // Use XMLHttpRequest instead of fetch (more reliable for blobs from signed URLs)
      const blob = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.responseType = "blob";

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(xhr.response);
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${po.name}`));
          }
        };

        xhr.onerror = () => reject(new Error("Network error"));
        xhr.ontimeout = () => reject(new Error("Timeout"));
        xhr.send();
      });

      folder.file(po.name, blob);

      setProgress(Math.round(((i + 1) / quarterPOs.length) * 100));
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, `POs_${q}_${r.replace(/–/g, "-")}_${new Date().toLocaleDateString("en-GB")}.zip`);

    alert("Export successful! Check your downloads.");
  } catch (err) {
    console.error("Export failed:", err);
    alert(
      "Export failed. Possible causes:\n• One or more files are too large\n• Network interruption\n• Browser blocked the download\n\nTry downloading individual POs instead."
    );
  } finally {
    setExporting(false);
    setProgress(0);
    setShowExportModal(false);
  }
};

  const filteredPOs = poList.filter(applyFilter);

  const handlePreview = async (po) => {
    try {
      const url = await getDownloadURL(ref(storage, po.storagePath));
      setSelectedPO({ ...po, url });
    } catch (err) {
      alert("Failed to load preview.");
    }
  };

  if (loading) {
    return (
      <div className="upo-container">
        <div className="upo-loading">
          <div className="upo-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const purchaseInfo = () => {
    alert(
      "📄 PURCHASE ORDERS PAGE\n\n" +
      "This page shows all your Purchase Orders (POs) send by supplier's.\n\n" +
      "What you can do here:\n" +
      "👁️ View: Click the eye icon or card to preview the PDF below.\n" +
      "⬇ Download: Click the download icon to open/save individual PO.\n" +
      "📦 Export: Download all POs from the current financial quarter as a ZIP file\n\n" +
      "Filters:\n" +
      "• All: Shows every document\n" +
      "• This Week: Only POs from last 7 days\n" +
      "• This Month: Only POs from current month\n\n" +
      "Current Financial Quarter: " + q + " (" + r + ")\n\n" +
      "Tip: Use the Export button to get all quarterly POs in one ZIP!"
    );
  };
  return (
    <div className="upo-container">
      <div className="upo-header">
        <h1>Purchase Orders
            <MdInfo className="icon-i" style={{marginLeft:'5px'}} onClick={purchaseInfo} />
        </h1>
      </div>

      <div className="upo-controls">
        <div className="upo-filter">
          <label className="upo-label">Filter:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="ALL">All</option>
            <option value="WEEK">This Week</option>
            <option value="MONTH">This Month</option>
          </select>
        </div>
        {/* <button className="upo-export-btn" onClick={openExportModal}>
          📦 Export {q} ({getQuarterPOs().length})
        </button> */}
      </div>

      <div className="upo-grid">
        {filteredPOs.length === 0 ? (
          <div className="upo-empty">
            <div className="upo-empty-icon">📄</div>
            <h3>No Purchase Orders</h3>
            <p>No documents found.</p>
          </div>
        ) : (
          filteredPOs.map((po) => (
            <div
              key={po.id}
              className={`upo-card ${selectedPO?.id === po.id ? "upo-active" : ""}`}
            >
              <div className="upo-card-actions">
                <button
                  className="upo-view-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(po);
                  }}
                  title="Preview"
                >
                  👁️
                </button>
                <button
                  className="upo-download-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    getDownloadURL(ref(storage, po.storagePath))
                      .then((url) => window.open(url, "_blank"))
                      .catch(() => alert("Open failed"));
                  }}
                  title="Open/Download"
                >
                  ⬇
                </button>
              </div>
              <div className="upo-card-body" onClick={() => handlePreview(po)}>
                <h4 className="upo-filename" title={po.name}>{po.name}</h4>
                <div className="upo-date">
                  {new Date(po.uploadedAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Inline Preview */}
      {!isMobile && selectedPO && (
        <div className="upo-preview-section">
          <div className="upo-preview-header">
            <h3>Preview: {selectedPO.name}</h3>
            <div className="upo-preview-actions">
              <a href={selectedPO.url} download={selectedPO.name} className="upo-download-full">
                💾 Download
              </a>
              <button className="upo-close-preview" onClick={() => setSelectedPO(null)}>✕</button>
            </div>
          </div>
          <iframe src={selectedPO.url} className="upo-pdf-iframe" title="Preview" />
        </div>
      )}

      {/* Mobile Full-Screen Preview Modal */}
      {isMobile && selectedPO && (
        <div className="upo-mobile-preview-overlay" onClick={() => setSelectedPO(null)}>
          <div className="upo-mobile-preview">
            <div className="upo-mobile-preview-header">
              <h3>{selectedPO.name}</h3>
              <button className="upo-close-preview" onClick={() => setSelectedPO(null)}>✕</button>
            </div>
            <iframe src={selectedPO.url} className="upo-mobile-pdf" title="Preview" />
            <div className="upo-mobile-actions">
              <a href={selectedPO.url} download={selectedPO.name} className="upo-download-full">
                💾 Download PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="upo-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="upo-export-modal" onClick={(e) => e.stopPropagation()}>
            <button className="upo-modal-close" onClick={() => setShowExportModal(false)}>×</button>
            <h3>Export {q} POs ({r})</h3>
            <p>{quarterPOs.length} files</p>
            {!exporting ? (
              <div className="upo-export-list">
                {quarterPOs.slice(0, 8).map((po) => (
                  <div key={po.id}>📄 {po.name}</div>
                ))}
                {quarterPOs.length > 8 && <div className="upo-more">+{quarterPOs.length - 8} more</div>}
              </div>
            ) : (
              <div className="upo-progress">
                <div className="upo-progress-bar">
                  <div className="upo-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <p>{progress}%</p>
              </div>
            )}
            <div className="upo-modal-actions">
              <button className="upo-btn-cancel" onClick={() => setShowExportModal(false)}>Cancel</button>
              <button className="upo-btn-export" onClick={exportPOsAsZip} disabled={exporting}>
                {exporting ? "Exporting..." : "Download ZIP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPurchaseOrders;